import { CdnContext } from '@/contexts/CdnContext';
import { ChatRoomDisplayContext } from '@/contexts/ChatRoomDisplayContext';
import { CommonConfigContext } from '@/contexts/ConfigContext';
import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { getGameState } from '@/modules/games/selector';
import { t } from 'i18next';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getMainGiftState, getMainUserState } from '../modules/main/selector';
import { LocalStorageKey } from './storage/useLocalStorage';
import { GiftInfo } from './useGift';
import { useNavigatorOnLine } from './useNavigatorOnLine';

enum MSG_TYPE {
    Login = 1,
    JoinRoom,
    RenewToken,
    Gift,
    Heartbeat,
    Error,
    LeaveRoom,
}

export type ChatInfo = {
    message: string;
    self?: boolean;
    key?: string;
    error?: boolean;
};

const RETRY_MAX_COUNT = 5;
const useChatRoom = () => {
    const { Username, LoginToken, PlayerID, IsTrial, IsInternalTest } =
        useSelector(getMainUserState);
    const { notToReconnect } = useSelector(getGameState);

    const { chatWs } = useContext(CdnContext);
    const { lobbyGiftEnable } = useSelector(getMainGiftState);
    const { isChatRoomOn } = useContext(GlobalDataContext);
    const giftSocket = useRef<WebSocket>();
    const [placeholder, setPlaceholder] = useState('');
    const [showChatPanel, setShowChatPanel] = useState(false);
    const [disable, setDisable] = useState(false);
    const displayTimerRef = useRef<NodeJS.Timeout>();
    const keepAliveRef = useRef<NodeJS.Timeout>();
    const { chat: chatConfig } = useContext(CommonConfigContext);
    const msgList = useRef<Array<ChatInfo>>([]);
    const [msgDisplay, setMsgDisplay] = useState<Array<ChatInfo>>([]);
    const [displayLength, setDisplayLength] = useState<number>(20);
    const [intervalTime, setIntervalTime] = useState<number>(1000);
    const [changeNum, setChangeNum] = useState<number>(0);
    const [allowSendMsg, setAllowSendMsg] = useState<boolean>(false);
    const [pendingGifts, setPendingGifts] = useState<Array<GiftInfo>>([]);
    const [giftsDisplay, setGiftsDisplay] = useState<Array<GiftInfo>>([]);
    const showOtherPlayerGiftRef = useRef<boolean>(true);
    const retryCount = useRef<number>(0);
    const notToReconnectRef = useRef<boolean>(false);
    const joinedRoomHostId = useRef<number>();
    const { showOtherPlayerGift, isChatPanelEnabled, setLoginReady, joinRoom } =
        useContext(ChatRoomDisplayContext);
    const onlineState = useNavigatorOnLine();

    useEffect(() => {
        if (chatConfig) {
            setDisplayLength(chatConfig.displayLength ?? 20);
            setIntervalTime(chatConfig.intervalTime ?? 1000);
        }

        if (IsTrial) {
            setDisable(true);
            setPlaceholder(t('trial_account'));
        } else {
            setDisable(false);
            setPlaceholder(t('chat_room.chat_room_prompt'));
        }
    }, [IsTrial]);

    useEffect(() => {
        showOtherPlayerGiftRef.current = showOtherPlayerGift;
        if (!showOtherPlayerGift && giftsDisplay.length > 0) {
            setGiftsDisplay(list => list?.filter(g => g.self));
        }
        if (!showOtherPlayerGift && pendingGifts.length > 0) {
            setPendingGifts(list => list?.filter(g => g.self));
        }
    }, [showOtherPlayerGift]);

    /**
     * chatEnabled: host list
     * isChatRoomOn: Game Feature
     * isChatRoomEnabled: side menu switch
     * */
    useEffect(() => {
        setShowChatPanel(isChatRoomOn == true && isChatPanelEnabled);
    }, [isChatRoomOn, isChatPanelEnabled]);

    useEffect(() => {
        sendGiftMessage(MSG_TYPE.RenewToken);
    }, [LoginToken]);
    useEffect(() => {
        notToReconnectRef.current = notToReconnect;
        if (notToReconnect) {
            socketClose();
        }
    }, [notToReconnect]);

    useEffect(() => {
        if (
            lobbyGiftEnable != undefined &&
            lobbyGiftEnable &&
            chatWs &&
            Username &&
            LoginToken &&
            PlayerID
        ) {
            socketCreate();
            return () => socketClose();
        }
    }, [chatWs, lobbyGiftEnable]);

    const socketCreate = () => {
        if (notToReconnectRef.current) {
            return;
        }
        giftSocket.current = new WebSocket(chatWs);
        giftSocket.current.onopen = () => {
            console.log('chatroom::gift::onopen');
            retryCount.current = 0;
            sendGiftMessage(MSG_TYPE.Login);
        };
        giftSocket.current.onmessage = event => {
            const data = JSON.parse(event.data);
            console.log('chatroom::gift::message', data);
            if (data.response_code == 0) {
                switch (data.response_msg_type) {
                    case MSG_TYPE.Login:
                        setLoginReady(true);
                        setAllowSendMsg(true);
                        if (joinedRoomHostId) {
                            sendGiftMessage(
                                MSG_TYPE.JoinRoom,
                                joinedRoomHostId.current
                            );
                        }
                        break;
                    case MSG_TYPE.Heartbeat:
                        setAllowSendMsg(true);
                        giftEvent(data.data);
                        keepAliveRef.current &&
                            clearTimeout(keepAliveRef.current);
                        keepAliveRef.current = setTimeout(
                            () => {
                                setAllowSendMsg(false);
                            },
                            60 * 1000 * 3
                        );
                        break;
                    case MSG_TYPE.Error:
                        break;
                    case MSG_TYPE.Gift:
                        giftEvent(data.data);
                        break;
                }
            }
        };
        giftSocket.current.onclose = () => {
            if (retryCount.current < RETRY_MAX_COUNT) {
                if (giftSocket.current?.readyState == WebSocket.CLOSED) {
                    setAllowSendMsg(false);
                    setTimeout(() => {
                        if (
                            giftSocket.current?.readyState == WebSocket.CLOSED
                        ) {
                            retryCount.current += 1;
                            socketCreate();
                        }
                    }, 10000);
                }
            }
        };
    };

    const sendGiftMessage = (msgType: MSG_TYPE, hostId?: number) => {
        if (
            giftSocket.current &&
            giftSocket.current.readyState == WebSocket.OPEN
        ) {
            const msg = {
                request_msg_type: msgType,
                player_name: Username,
                player_id: PlayerID,
                token: LoginToken.slice(0, 16),
                join_host_id: hostId ?? 0,
            };
            console.log('chatroom::gift::send', msg);
            giftSocket.current?.send(JSON.stringify(msg));
        }
    };

    const socketClose = () => {
        giftSocket.current?.close();
    };

    const onFocus = () => {
        // const ts = localStorage.getItem(LocalStorageKey.LastBetTs);
        // if (!IsTrial && Number(ts) + 5 * 60 * 1000 >= currentTimeStamp()-8*3600*1000) {
        //     setDisable(false);
        // } else {
        //     if (IsTrial) {
        //         addErrorMessage(t('trial_account'));
        //     } else {
        //         addErrorMessage(t('chat_room.place_bet'));
        //     }
        //     setDisable(true);
        // }
    };

    const giftEvent = (giftJson: {
        gift_id: number;
        host_id: number;
        player_id: string;
        display_interval: number;
    }) => {
        if (giftJson.gift_id != undefined) {
            if (IsInternalTest || (!IsInternalTest && giftJson.gift_id > 0)) {
                const gift = {
                    giftId: giftJson.gift_id,
                    hostId: giftJson.host_id,
                    self: giftJson.player_id == PlayerID.toString(),
                    display_interval: giftJson.display_interval,
                    key: uuidv4(),
                };
                if (giftJson.player_id == PlayerID.toString()) {
                    // setGiftsDisplay(list => list?.concat(gift));
                    setPendingGifts(list => list?.concat(gift));
                } else if (showOtherPlayerGiftRef.current) {
                    setPendingGifts(list => list?.concat(gift));
                }
            }
        }
    };
    // //=================== fake gift ================
    // const tmp = useRef<number>(0);
    // useEffect(() => {
    //     const isDevelopment = process.env.NODE_ENV === 'development';
    //     let id: NodeJS.Timeout;
    //     if (joinedRoomHostId && isDevelopment) {
    //         id = setInterval(() => {
    //             giftEvent({
    //                 gift_id: (tmp.current % 8) + 1,
    //                 host_id: 901,
    //                 display_interval: 3000,
    //                 player_id: '21616',
    //             });
    //             tmp.current++;
    //         }, 200);
    //     }
    //     return () => {
    //         if (id) clearInterval(id);
    //     };
    // }, [joinedRoomHostId, giftEvent]);
    // //===================

    useEffect(() => {
        if (pendingGifts.length > 2) {
            //if waiting list long, slow down update frequency
            if (giftsDisplay.length <= 8) {
                let newPendingGifts = pendingGifts.concat([]);
                const gift = newPendingGifts.splice(
                    0,
                    10 - giftsDisplay.length
                );
                if (gift && gift.length > 0) {
                    setGiftsDisplay(list => list.concat(gift));
                    setPendingGifts(newPendingGifts);
                }
            }
        } else if (giftsDisplay.length < 10 && pendingGifts.length > 0) {
            let newPendingGifts = pendingGifts.concat([]);
            const gift = newPendingGifts.splice(0, 10 - giftsDisplay.length);
            if (gift && gift.length > 0) {
                setGiftsDisplay(list => list.concat(gift));
                setPendingGifts(newPendingGifts);
            }
        }
    }, [pendingGifts, giftsDisplay]);

    // const joinRoom = (hostId: number) => {
    //     setGiftsDisplay([]);
    //     setPendingGifts([]);
    //     setJoinedRoomHostId(hostId);

    //     if (retryCount.current >= RETRY_MAX_COUNT) {
    //         socketCreate();
    //     }

    //     sendGiftMessage(MSG_TYPE.JoinRoom, hostId);
    // };

    // const leaveRoom = () => {
    //     setJoinedRoomHostId(undefined);
    //     sendGiftMessage(MSG_TYPE.LeaveRoom);
    // };
    useEffect(() => {
        if (joinRoom) {
            setGiftsDisplay([]);
            setPendingGifts([]);
            joinedRoomHostId.current = joinRoom.hostId;

            if (giftSocket.current?.readyState == WebSocket.CLOSED) {
                retryCount.current = 0;
                socketCreate();
            }
            sendGiftMessage(MSG_TYPE.JoinRoom, joinRoom.hostId);
        } else {
            joinedRoomHostId.current = undefined;
            sendGiftMessage(MSG_TYPE.LeaveRoom);
        }
    }, [joinRoom]);
    useEffect(() => {
        if (onlineState) {
            if (giftSocket.current?.readyState == WebSocket.CLOSED) {
                retryCount.current = 0;
                socketCreate();
            }
        }
    }, [onlineState]);
    const sendMessage = (message: string) => {
        if (!message) {
            return false;
        } else if (IsTrial) {
            addErrorMessage(t('trial_account'));
            return false;
        } else {
            const ts = localStorage.getItem(LocalStorageKey.LastBetTs);
            if (Number(ts) + 5 * 60 * 1000 < Date.now()) {
                addErrorMessage(t('chat_room.place_bet'));
                return false;
            }
        }
        // if (MC.current) MC.current.Send(message);
        return true;
    };

    const shortName = (name: string) => {
        const pattern = /@.*/;
        const tmp = name.replace(pattern, '');
        return '*' + tmp.slice(-3);
    };

    const removeMsg = useCallback((key?: string) => {
        setMsgDisplay(list => list?.filter(g => g.key != key));
    }, []);

    const addErrorMessage = (errorMsg: string) => {
        let newMsg = {
            message: `[${t('system.you').toUpperCase()}] ${errorMsg}`,
            key: uuidv4(),
            self: true,
            error: true,
        };
        msgList.current = [...msgList.current, newMsg];
        setChangeNum(n => n + 1);
    };

    // const addMessage = (chatInfo: ChatInfo) => {
    //     msgList.current = [...msgList.current, chatInfo];
    //     setChangeNum(n => n + 1);
    // };

    const addMsgToDisplay = () => {
        if (msgList.current.length > 0) {
            const item = msgList.current.shift();
            item && setMsgDisplay(displayList => [item, ...displayList]);
            if (displayTimerRef.current) {
                clearTimeout(displayTimerRef.current);
            }
            displayTimerRef.current = setTimeout(() => {
                addMsgToDisplay();
            }, intervalTime);
        } else {
            if (displayTimerRef.current) {
                clearTimeout(displayTimerRef.current);
                displayTimerRef.current = undefined;
            }
        }
    };

    useEffect(() => {
        if (
            msgList &&
            msgList.current.length > 0 &&
            msgDisplay.length < displayLength
        ) {
            addMsgToDisplay();
        }
    }, [changeNum]);

    return {
        disable,
        chatConfig,
        shortName,
        sendMessage,
        placeholder,
        msgDisplay,
        removeMsg,
        onFocus,
        showChatPanel,
        addErrorMessage,
        allowSendMsg,
        pendingGifts,
        setPendingGifts,
        giftsDisplay,
        setGiftsDisplay,
        socketClose,
    };
};
export default useChatRoom;
export type useChatRoomState = ReturnType<typeof useChatRoom>;
